import * as React from 'react'

import { Archive, ArrowLeft, SquarePen, X } from 'lucide-react'
import { observer } from 'mobx-react'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import { Loader } from '~/client/src/shared/components/Loader'
import { Badge } from '~/client/src/shared/components/shadcn-ui/badge'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Empty,
  EmptyAction,
  EmptyContent,
  EmptyHeader,
  EmptyTitle,
} from '~/client/src/shared/components/shadcn-ui/empty'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { resolveMentions } from '../core/protocol/mentionText'
import { Chat, SentMessage } from '../core/protocol/types'
import { ChatSession } from '../core/stores/Session'
import { WebContactDirectory } from '../web/directory'

// Avatar (32) + the gap: the separator starts exactly where the text does.
const SEPARATOR_INSET = 42

interface IProps {
  session: ChatSession
  directory: WebContactDirectory
  projectDateStore: ProjectDateStore
  onOpenChat: (chat: Chat) => void
  onNewChat: () => void
  onClose?: () => void
}

interface IRowProps {
  chat: Chat
  session: ChatSession
  directory: WebContactDirectory
  projectDateStore: ProjectDateStore
  onOpenChat: (chat: Chat) => void
}

const InboxRow = observer(function InboxRow({
  chat,
  session,
  directory,
  projectDateStore,
  onOpenChat,
}: IRowProps): React.ReactElement {
  const { inbox, contacts } = session

  const formatTime = (timestamp: number): string =>
    projectDateStore.isToday(timestamp)
      ? projectDateStore.getTimeToDisplay(timestamp)
      : projectDateStore.getPronounOrMonthAndDayString(timestamp)

  const subtitle = chat.isGroup
    ? directory.groupSubtitle(chat)
    : directory.directSubtitle(inbox.directChatRecipientId(chat))

  const renderPreview = (): React.ReactElement => {
    const lastMessage: SentMessage | undefined = inbox.lastMessageByChannel.get(
      chat.id,
    )

    if (!lastMessage) {
      return (
        <span className="tw:truncate tw:text-sm tw:text-muted-foreground">
          {Localization.translator.noMessagesYet}
        </span>
      )
    }

    const body = lastMessage.text
      ? resolveMentions(lastMessage.text, id => contacts.getFullName(id))
      : lastMessage.attachments.length
        ? Localization.translator.photoMessage
        : ''

    const authorPrefix =
      chat.isGroup && lastMessage.authorId !== session.config.userId
        ? `${
            contacts.get(lastMessage.authorId)?.firstName ||
            Localization.translator.user
          }: `
        : ''

    return (
      <span className="tw:truncate tw:text-sm tw:text-muted-foreground">
        {!!authorPrefix && (
          <span className="tw:font-semibold tw:text-foreground/80">
            {authorPrefix}
          </span>
        )}
        {body}
      </span>
    )
  }

  const recipientId = chat.isGroup
    ? undefined
    : inbox.directChatRecipientId(chat)
  const lastMessage = inbox.lastMessageByChannel.get(chat.id)
  const unreadCount = inbox.unreadCount(chat.id)

  return (
    <div
      className="tw:flex tw:cursor-pointer tw:items-center tw:py-2.5"
      onClick={() => onOpenChat(chat)}
    >
      <Avatar
        src={chat.avatar}
        size={AvatarSize.Small}
        avatarType={chat.isGroup ? AvatarType.Company : AvatarType.User}
        isOnline={
          chat.isGroup ? null : !!recipientId && inbox.isOnline(recipientId)
        }
      />
      <div className="tw:ml-2.5 tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
        <div className="tw:flex tw:items-center">
          <span
            className={`tw:min-w-0 tw:flex-1 tw:truncate tw:font-semibold ${
              unreadCount ? 'tw:text-primary' : 'tw:text-foreground'
            }`}
          >
            {chat.name || Localization.translator.conversation}
          </span>
          {!!lastMessage && (
            <span className="tw:shrink-0 tw:pl-2 tw:text-xs tw:text-muted-foreground">
              {formatTime(lastMessage.createdAt)}
            </span>
          )}
        </div>
        {!!subtitle && (
          <span className="tw:mt-px tw:truncate tw:text-xs tw:text-muted-foreground">
            {subtitle}
          </span>
        )}
        <div className="tw:mt-0.5 tw:flex tw:items-center">
          <div className="tw:flex tw:min-w-0 tw:flex-1">{renderPreview()}</div>
          {unreadCount > 0 && (
            <Badge className="tw:ml-2.5 tw:shrink-0">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
})

function InboxScreen({
  session,
  directory,
  projectDateStore,
  onOpenChat,
  onNewChat,
  onClose,
}: IProps): React.ReactElement {
  const [isShowingArchived, setIsShowingArchived] = React.useState(false)
  const { inbox } = session

  const renderRows = (chats: readonly Chat[]): React.ReactElement[] =>
    chats.map((chat, index) => (
      <React.Fragment key={chat.id}>
        {index > 0 && (
          <div
            className="tw:h-px tw:bg-border"
            style={{ marginLeft: SEPARATOR_INSET }}
          />
        )}
        <InboxRow
          chat={chat}
          session={session}
          directory={directory}
          projectDateStore={projectDateStore}
          onOpenChat={onOpenChat}
        />
      </React.Fragment>
    ))

  if (inbox.status === 'loading' || inbox.status === 'idle') {
    return <Loader hint={Localization.translator.loading} />
  }

  if (inbox.status === 'error') {
    return (
      <Empty className="tw:h-full">
        <EmptyHeader>
          <EmptyTitle>
            {Localization.translator.couldNotLoadYourInbox}
          </EmptyTitle>
        </EmptyHeader>
        <EmptyContent>
          <EmptyAction variant="link" onClick={() => inbox.refresh()}>
            {Localization.translator.tryAgain}
          </EmptyAction>
        </EmptyContent>
      </Empty>
    )
  }

  if (isShowingArchived) {
    return (
      <div className="tw:flex tw:h-full tw:flex-col">
        <Button
          variant="primary-ghost"
          onClick={() => setIsShowingArchived(false)}
          className="tw:mx-1.5 tw:mb-2.5 tw:mt-5 tw:self-start"
        >
          <ArrowLeft />
          {Localization.translator.archived}
        </Button>
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-4">
          {inbox.sortedArchivedChats.length ? (
            renderRows(inbox.sortedArchivedChats)
          ) : (
            <Empty className="tw:h-full">
              <EmptyHeader>
                <EmptyTitle>
                  {Localization.translator.noArchivedConversations}
                </EmptyTitle>
              </EmptyHeader>
            </Empty>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="tw:flex tw:h-full tw:flex-col">
      <div className="tw:flex tw:items-center tw:gap-1.5 tw:px-4 tw:pt-3">
        {!!onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X />
          </Button>
        )}
        <SearchInput
          value={inbox.searchKey}
          onChange={inbox.setSearchKey}
          className="tw:w-full tw:flex-1"
        />
        <Button variant="primary-ghost" size="icon" onClick={onNewChat}>
          <SquarePen />
        </Button>
      </div>
      <div className="tw:mt-2.5 tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-4">
        {inbox.sortedChats.length ? (
          renderRows(inbox.sortedChats)
        ) : (
          <Empty className="tw:h-full">
            <EmptyHeader>
              <EmptyTitle>
                {inbox.searchKey
                  ? Localization.translator.noConversationsFound
                  : Localization.translator.inboxIsEmpty}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}
      </div>
      {!!inbox.archivedChats.length && (
        <div className="tw:border-0 tw:border-t tw:border-solid tw:border-border tw:p-1.5">
          <Button
            variant="ghost"
            onClick={() => setIsShowingArchived(true)}
            className="tw:w-full tw:justify-start"
          >
            <Archive />
            {`${Localization.translator.archived} (${inbox.archivedChats.length})`}
          </Button>
        </div>
      )}
    </div>
  )
}

export default observer(InboxScreen)
